/**
 * The on-screen viewfinder, defined in *display* space (a fraction of the
 * displayed video box, with a fixed wide aspect ratio). Defining it on screen —
 * rather than as a fraction of the source frame — keeps it a wide landscape band
 * regardless of the camera's native orientation. (A phone delivers a portrait
 * stream, so source-frame fractions would come out square.)
 *
 * Shared by the ScannerModal reticle (which draws this box) and the WASM decoder
 * (which maps it back to source pixels via `sourceCropRect` and crops there), so
 * the box you see is exactly the region that gets decoded.
 */
export const VIEWFINDER = {
  /** Fraction of the displayed video width the box spans. */
  widthFraction: 0.9,
  /** width / height. ~1.6 matches an ID-1 card (driver's license) with buffer. */
  aspectRatio: 1.6,
} as const;

export interface Rect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const EMPTY: Rect = { x: 0, y: 0, width: 0, height: 0 };

/** The viewfinder rect in display (CSS) pixels, centered in the video box. */
export function viewfinderRect(
  displayWidth: number,
  displayHeight: number,
): Rect {
  if (displayWidth <= 0 || displayHeight <= 0) return EMPTY;
  let width = displayWidth * VIEWFINDER.widthFraction;
  let height = width / VIEWFINDER.aspectRatio;
  // Never taller than the display; shrink width to preserve aspect if it would be.
  if (height > displayHeight) {
    height = displayHeight;
    width = height * VIEWFINDER.aspectRatio;
  }
  return {
    x: (displayWidth - width) / 2,
    y: (displayHeight - height) / 2,
    width,
    height,
  };
}

/**
 * Map the viewfinder (display space) to source-pixel coordinates through the
 * video's `object-fit: cover` transform, so the WASM decoder crops exactly the
 * region shown in the reticle — correct for any camera orientation. Returns a
 * zero rect (guard on `width > 0`) when dimensions aren't ready.
 */
export function sourceCropRect(
  displayWidth: number,
  displayHeight: number,
  naturalWidth: number,
  naturalHeight: number,
): Rect {
  if (
    displayWidth <= 0 ||
    displayHeight <= 0 ||
    naturalWidth <= 0 ||
    naturalHeight <= 0
  ) {
    return EMPTY;
  }
  const vf = viewfinderRect(displayWidth, displayHeight);
  // cover: scale the source up until it covers the display, then center-crop.
  const scale = Math.max(
    displayWidth / naturalWidth,
    displayHeight / naturalHeight,
  );
  const offsetX = (naturalWidth * scale - displayWidth) / 2;
  const offsetY = (naturalHeight * scale - displayHeight) / 2;
  const x = Math.max(0, Math.round((vf.x + offsetX) / scale));
  const y = Math.max(0, Math.round((vf.y + offsetY) / scale));
  return {
    x,
    y,
    width: Math.min(naturalWidth - x, Math.round(vf.width / scale)),
    height: Math.min(naturalHeight - y, Math.round(vf.height / scale)),
  };
}
